import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { wishlist } from "@/lib/wishlist";

const Ctx = createContext({ user: null, loading: true, login: () => {}, logout: () => {}, refresh: () => {} });

export const useCustomer = () => useContext(Ctx);

export function CustomerAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const syncTimer = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/customer/me");
      setUser(data);
      return data;
    } catch {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: if returning from OAuth, AuthCallback exchanges the session_id first.
    if (window.location.hash?.includes("session_id=")) { setLoading(false); return; }
    refresh();
  }, [refresh]);

  // merge local wishlist with the server copy on sign-in, then keep the server in sync
  useEffect(() => {
    if (!user) return undefined;
    let unsub;
    (async () => {
      try {
        const { data: remote } = await api.get("/customer/wishlist");
        const merged = [...remote, ...wishlist.items().filter((x) => !remote.some((r) => r.id === x.id))];
        wishlist.replace(merged);
        await api.put("/customer/wishlist", { items: merged });
      } catch { /* offline */ }
      unsub = wishlist.subscribe(() => {
        clearTimeout(syncTimer.current);
        syncTimer.current = setTimeout(() => api.put("/customer/wishlist", { items: wishlist.items() }).catch(() => {}), 600);
      });
    })();
    return () => unsub?.();
  }, [user]);

  const login = (returnTo = "/account") => {
    sessionStorage.setItem("rr_return_to", returnTo);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + returnTo;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    await api.post("/customer/logout").catch(() => {});
    setUser(null);
  };

  return <Ctx.Provider value={{ user, loading, login, logout, refresh, setUser }}>{children}</Ctx.Provider>;
}
